import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TaskDTO } from "../../models/Task.model";
import {
  getTasks,
  getTaskDetails,
  createTask,
  deleteTask,
  setTaskStatus,
  snoozeTask,
} from "../thunks/taskThunks";

// פונקציה עזר לחלוקת משימות לפי תאריכים
const categorizeTasks = (tasks: TaskDTO[]) => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // התחלת היום (00:00:00)
  const tomorrowStart = new Date(
    tomorrow.getFullYear(),
    tomorrow.getMonth(),
    tomorrow.getDate(),
  );
  const dayAfterTomorrow = new Date(tomorrow);
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);
  const dayAfterTomorrowStart = new Date(
    dayAfterTomorrow.getFullYear(),
    dayAfterTomorrow.getMonth(),
    dayAfterTomorrow.getDate(),
  );

  const todayTasks: TaskDTO[] = [];
  const tomorrowTasks: TaskDTO[] = [];
  const otherTasks: TaskDTO[] = [];

  tasks.forEach((task) => {
    const dueDate = new Date(task.task_DueDateTime);

    if (dueDate < tomorrowStart) {
      // משימות עד היום (כולל היום ועבר)
      todayTasks.push(task);
    } else if (dueDate >= tomorrowStart && dueDate < dayAfterTomorrowStart) {
      tomorrowTasks.push(task);
    } else {
      otherTasks.push(task);
    }
  });

  return { todayTasks, tomorrowTasks, otherTasks };
};

interface TaskState {
  tasks: TaskDTO[];
  todayTasks: TaskDTO[];
  tomorrowTasks: TaskDTO[];
  otherTasks: TaskDTO[];
  selectedTask: TaskDTO | null;
  loading: boolean;
  detailsLoading: boolean;
  operationLoading: boolean;
  error: string | null;
  success: boolean;
  message: string;
  initialized: boolean;
  currentProjectId: number | null;
}

const initialState: TaskState = {
  tasks: [],
  todayTasks: [],
  tomorrowTasks: [],
  otherTasks: [],
  selectedTask: null,
  loading: false,
  detailsLoading: false,
  operationLoading: false,
  error: null,
  success: false,
  message: "",
  initialized: false,
  currentProjectId: null,
};

const taskSlice = createSlice({
  name: "task",
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.success = false;
      state.message = "";
    },
    clearSelectedTask: (state) => {
      state.selectedTask = null;
    },
    clearTasks: (state) => {
      state.tasks = [];
      state.todayTasks = [];
      state.tomorrowTasks = [];
      state.otherTasks = [];
      state.selectedTask = null;
      state.initialized = false;
      state.currentProjectId = null;
    },
    setCurrentProjectId: (state, action: PayloadAction<number>) => {
      if (state.currentProjectId !== action.payload) {
        state.currentProjectId = action.payload;
        state.tasks = [];
        state.todayTasks = [];
        state.tomorrowTasks = [];
        state.otherTasks = [];
        state.initialized = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Tasks
      .addCase(getTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
        // איפוס המערכים בעת טעינה חדשה
        state.tasks = [];
        state.todayTasks = [];
        state.tomorrowTasks = [];
        state.otherTasks = [];
      })
      .addCase(getTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload.tasks || [];

        // חלוקת המשימות לפי תאריכים
        const { todayTasks, tomorrowTasks, otherTasks } = categorizeTasks(
          state.tasks,
        );
        state.todayTasks = todayTasks;
        state.tomorrowTasks = tomorrowTasks;
        state.otherTasks = otherTasks;

        state.initialized = true;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(getTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה בקבלת המשימות";
      })

      // Get Task Details
      .addCase(getTaskDetails.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
      })
      .addCase(getTaskDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.selectedTask = action.payload.task;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(getTaskDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action.payload || "שגיאה בקבלת פרטי המשימה";
      })

      // Create Task
      .addCase(createTask.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.success = true;
        state.message = action.payload.message;
        // הוספת המשימה החדשה לרשימה (יש לבדוק אם צריך לרענן את הרשימה)
      })
      .addCase(createTask.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload || "שגיאה ביצירת המשימה";
      })

      // Delete Task
      .addCase(deleteTask.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.success = true;
        state.message = action.payload.message;
        // הסרת המשימה מהרשימה (יש לבדוק אם צריך לרענן את הרשימה)
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload || "שגיאה במחיקת המשימה";
      })

      // Set Task Status
      .addCase(setTaskStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(setTaskStatus.fulfilled, (state, action) => {
        state.success = true;
        state.message = action.payload.message;

        // עדכון הסטטוס ברשימה המקומית
        const { taskID, isCompleted } = action.meta.arg;

        // עדכון ברשימה הכללית
        const taskIndex = state.tasks.findIndex(
          (task) => task.task_ID === taskID,
        );
        if (taskIndex !== -1) {
          state.tasks[taskIndex].task_IsCompleted = isCompleted;
        }

        // עדכון במערכים המקטגריים
        [state.todayTasks, state.tomorrowTasks, state.otherTasks].forEach(
          (taskArray) => {
            const index = taskArray.findIndex(
              (task) => task.task_ID === taskID,
            );
            if (index !== -1) {
              taskArray[index].task_IsCompleted = isCompleted;
            }
          },
        );
      })
      .addCase(setTaskStatus.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload || "שגיאה בעדכון סטטוס המשימה";
      })
      .addCase(snoozeTask.pending, (state) => {
        state.error = null;
      })
      .addCase(snoozeTask.fulfilled, (state, action) => {
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(snoozeTask.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload || "שגיאה בדחיית המשימה";
      });
  },
});

// Selectors
export const selectTasks = (state: { task: TaskState }) => state.task.tasks;
export const selectTodayTasks = (state: { task: TaskState }) =>
  state.task.todayTasks;
export const selectTomorrowTasks = (state: { task: TaskState }) =>
  state.task.tomorrowTasks;
export const selectOtherTasks = (state: { task: TaskState }) =>
  state.task.otherTasks;
export const selectSelectedTask = (state: { task: TaskState }) =>
  state.task.selectedTask;
export const selectTasksLoading = (state: { task: TaskState }) =>
  state.task.loading;
export const selectTaskDetailsLoading = (state: { task: TaskState }) =>
  state.task.detailsLoading;
export const selectTaskOperationLoading = (state: { task: TaskState }) =>
  state.task.operationLoading;
export const selectTaskError = (state: { task: TaskState }) => state.task.error;
export const selectTaskSuccess = (state: { task: TaskState }) =>
  state.task.success;
export const selectTaskMessage = (state: { task: TaskState }) =>
  state.task.message;
export const selectTasksInitialized = (state: { task: TaskState }) =>
  state.task.initialized;
export const selectCurrentProjectId = (state: { task: TaskState }) =>
  state.task.currentProjectId;

// Selector עזר לסטטיסטיקות משימות - עם memoization
export const selectTasksStats = (state: { task: TaskState }) => {
  const { tasks, todayTasks, tomorrowTasks, otherTasks } = state.task;

  return {
    total: tasks.length,
    today: todayTasks.length,
    tomorrow: tomorrowTasks.length,
    other: otherTasks.length,
    todayCompleted: todayTasks.filter((task) => task.task_IsCompleted).length,
    tomorrowCompleted: tomorrowTasks.filter((task) => task.task_IsCompleted)
      .length,
    otherCompleted: otherTasks.filter((task) => task.task_IsCompleted).length,
  };
};

export const {
  clearMessages,
  clearSelectedTask,
  clearTasks,
  setCurrentProjectId,
} = taskSlice.actions;

export default taskSlice.reducer;
